import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queryKeys';
import {
  userService,
  type CreateUserRequest,
  type UpdateUserRequest,
} from '@/services/userService';
import { roleService, type Role } from '@/services/roleService';

// Users list. Also fetches each user's roles in parallel so the table can
// render role badges without a separate query per row. Kept inside the list
// query so list + roles invalidate together on mutations.
// `includeRoles` skips the per-user roles fetch when the caller lacks
// `roles.view` (the endpoint is roles.view-gated → would otherwise 403 N times).
// It is part of the query key so a mid-session permission change refetches.
// `enabled` lets cross-module callers (e.g. the ticket assignee dropdowns) skip
// the fetch when they lack `users.view` — same reasoning as `includeRoles`.
export function useUsersList(
  page: number,
  pageSize: number,
  search: string,
  includeRoles = true,
  enabled = true,
) {
  return useQuery({
    queryKey: [...queryKeys.users.list(page, pageSize, search), { includeRoles }],
    queryFn: async () => {
      const response = await userService.getUsers(page, pageSize, search || undefined);
      if (!includeRoles) {
        return { ...response, rolesMap: {} as Record<string, Role[]> };
      }
      const rolesEntries = await Promise.all(
        response.items.map(async (user) => {
          try {
            const userRoles = await roleService.getUserRoles(user.id);
            return [user.id, userRoles] as const;
          } catch {
            return [user.id, [] as Role[]] as const;
          }
        }),
      );
      return {
        ...response,
        rolesMap: Object.fromEntries(rolesEntries) as Record<string, Role[]>,
      };
    },
    enabled,
  });
}

export function useUserRoles(userId: string | null) {
  return useQuery({
    queryKey: queryKeys.roles.forUser(userId ?? ''),
    queryFn: () => roleService.getUserRoles(userId as string),
    enabled: !!userId,
  });
}

export function useCreateUser(options?: { onError?: (error: unknown) => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateUserRequest) => userService.createUser(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
    onError: options?.onError,
  });
}

export function useUpdateUser(options?: { onError?: (error: unknown) => void }) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userService.updateUser(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
    onError: options?.onError,
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.users.all }),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) =>
      userService.resetPassword(id, newPassword),
  });
}

export function useAssignRolesToUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
      roleService.assignRolesToUser(userId, roleIds),
    onSuccess: (_data, { userId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.roles.forUser(userId) });
      qc.invalidateQueries({ queryKey: queryKeys.users.all });
    },
  });
}
